import { SETTINGS_NAV_ROWS, sectionById } from '@elva/ui';
import {
  AI_TEAM_CHILDREN,
  getAiTeamChildFromPath,
  getNavKeyFromPath,
  SIDEBAR_NAV,
} from '@/app/config/nav';

/**
 * The settings label comes from the nav rows themselves (amendment 20 §2.1).
 *
 * It used to come from a hand-written map that had gone stale — it listed routes
 * that no longer exist (`/settings/general-info`, `/settings/insurance-network`)
 * and none of the pages built since, so **every settings page fell through to the
 * default and rendered *Settings › Settings***. Deriving it from the one list that
 * already knows every route fixes the whole class rather than one page, and a
 * future page cannot regress it.
 */
function settingsLabelFor(pathname: string): string | undefined {
  for (const node of SETTINGS_NAV_ROWS) {
    if (node.kind === 'leaf' && node.route === pathname) return node.label;
    if (node.kind === 'group') {
      const child = node.children.find((c) => c.route === pathname);
      if (child) return child.label;
    }
  }
  return undefined;
}

function navLabelFromKey(key: string): string {
  return SIDEBAR_NAV.find((item) => item.key === key)?.label ?? key;
}

/**
 * A reminders editor is three deep: `Settings › Appointment Reminders › Before
 * the visit` (amendment 25 §1). The section's own title comes from the reminders
 * data rather than a second list of names beside it.
 */
function remindersEditorCrumb(pathname: string): string[] | undefined {
  const m = /^\/settings\/ai\/reminders\/(?:after\/)?([^/]+)(?:\/(?:messages|test))?$/.exec(pathname);
  if (m === null) return undefined;
  const key = m[1];
  if (key === undefined) return undefined;
  const section = sectionById(key);
  return section === undefined ? undefined : ['Settings', 'Appointment Reminders', section.title];
}

export function buildPageBreadcrumb(pathname: string, workspaceLabel: string): string[] {
  const editor = remindersEditorCrumb(pathname);
  if (editor !== undefined) return [workspaceLabel, ...editor];

  // Gap Filler rule + offer editors (amendment 26 §5–§6).
  // TC campaign builder (amendment 27 §5).
  if (pathname.startsWith('/treatment-intelligence/campaigns/')) {
    const editing = pathname.includes('/edit');
    return [workspaceLabel, 'Treatment Intelligence', 'Campaigns', editing ? 'Edit campaign' : 'New campaign'];
  }

  // The follow-up builder (amendment 28 §4).
  if (pathname.startsWith('/collections/follow-ups/')) {
    const editing = pathname.includes('/edit');
    return [workspaceLabel, 'Collections', 'Follow-ups', editing ? 'Edit follow-up' : 'New follow-up'];
  }

  if (pathname.startsWith('/settings/ai/gap-filler/rules/')) {
    return [workspaceLabel, 'Settings', 'Gap Filler', pathname.endsWith('/new') ? 'Add a rule' : 'Edit a rule'];
  }
  if (pathname.startsWith('/settings/ai/gap-filler/offer/')) {
    return [workspaceLabel, 'Settings', 'Gap Filler', 'The offer'];
  }

  if (pathname.startsWith('/settings')) {
    const pageLabel = settingsLabelFor(pathname);
    // `/settings` itself redirects, so a bare `Settings` is only ever a transient
    // frame rather than a page whose name we failed to find.
    return pageLabel === undefined ? [workspaceLabel, 'Settings'] : [workspaceLabel, 'Settings', pageLabel];
  }

  if (pathname.startsWith('/patients/') && pathname !== '/patients') {
    return [workspaceLabel, 'Patients'];
  }

  if (pathname === '/forms/create') {
    return [workspaceLabel, 'Forms', 'Create form'];
  }

  if (pathname === '/forms/templates') {
    return [workspaceLabel, 'Forms', 'Templates'];
  }

  const aiChild = getAiTeamChildFromPath(pathname);
  if (aiChild !== undefined) {
    const childLabel = AI_TEAM_CHILDREN.find((c) => c.key === aiChild)?.label ?? 'Agent';
    return [workspaceLabel, 'AI Team', childLabel];
  }

  const navKey = getNavKeyFromPath(pathname);
  return [workspaceLabel, navLabelFromKey(navKey)];
}
